import React from 'react'

// Horizontal-bar band renderer — drop-in alternative to RomBandDial for
// exercises where the curved arc dial doesn't fit (e.g. Heel Slide). Bands
// paint in order green → yellow → blue across the visible angle range; a
// small downward-pointing white triangle tick marks the live angle above
// the bar.
//
// Props:
//   liveAngle   — clinical degrees; drives tick position
//   bandRanges  — { green:[a,b], yellow:[a,b], blue:[a,b] } | null
//   angleMin    — left edge of the bar in degrees (default -10)
//   angleMax    — right edge of the bar in degrees (default 135)
//   showTick    — render the white triangle tick (default true)
//   height      — bar height in CSS px (default 36)

const DEFAULT_MIN = -10
const DEFAULT_MAX = 135

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

export default function LinearBandBar({
  liveAngle,
  bandRanges,
  angleMin = DEFAULT_MIN,
  angleMax = DEFAULT_MAX,
  showTick = true,
  height = 36,
}) {
  const span = angleMax - angleMin
  if (span <= 0) return null

  // Mapped RIGHT-to-LEFT: 0° (angleMin) sits on the RIGHT edge, larger
  // angles run leftward to angleMax on the LEFT edge. (Heel Slide reads
  // "straight"/extension on the right, "bent"/flexion on the left.) Mirrors
  // the patient app's LinearBandBar so the admin preview matches it.
  const pct = (deg) => {
    var clamped = clamp(deg, angleMin, angleMax)
    return ((angleMax - clamped) / span) * 100
  }

  const segmentFor = (range, color, opacity, key) => {
    if (!range || range.length < 2 || range[0] == null || range[1] == null) return null
    var lo = Math.min(range[0], range[1])
    var hi = Math.max(range[0], range[1])
    if (hi <= angleMin || lo >= angleMax) return null
    // pct() is reversed (right-to-left), so the lower angle maps to a
    // larger % than the higher angle — take the min as the left edge and
    // the absolute difference as the width.
    var pLo = pct(lo)
    var pHi = pct(hi)
    var left = Math.min(pLo, pHi)
    var width = Math.abs(pHi - pLo)
    if (width <= 0) return null
    return (
      <div
        key={key}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: left + '%',
          width: width + '%',
          background: color,
          opacity: opacity,
        }}
      />
    )
  }

  // Match RomBandDial palette exactly — same hex values + per-band
  // opacities. Admin lacks the tracpatchColors cssVar module, so the
  // hex codes are inlined. These mirror the patient-app TracPatch
  // session palette (tp-zone-* defaults in
  // physio_app_web/styles/tracpatch-exercise.css).
  const greenColor  = '#669d34'
  const yellowColor = '#fefb41'
  const blueColor   = '#bdb6b6'

  // Paint order: green first (widest), yellow on top, blue last (narrowest).
  // Matches RomBandDial composition so the visual pattern reads the same.
  const segments = []
  if (bandRanges) {
    var g = segmentFor(bandRanges.green,  greenColor,  0.85, 'green')
    var y = segmentFor(bandRanges.yellow, yellowColor, 0.9,  'yellow')
    var b = segmentFor(bandRanges.blue,   blueColor,   0.95, 'blue')
    if (g) segments.push(g)
    if (y) segments.push(y)
    if (b) segments.push(b)
  }

  var tickPct = (liveAngle == null) ? null : pct(liveAngle)

  // Vertical cell dividers — N-1 thin white lines evenly spaced across
  // the bar to give the ruler/ladder look. Rendered on top of the band
  // segments but below the tick. Tied to bar width (NOT degrees) so the
  // cell count stays visually consistent across exercises.
  var DIVISIONS = 14
  var dividers = []
  for (var i = 1; i < DIVISIONS; i++) {
    dividers.push(
      <div
        key={'div-' + i}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 'calc(' + (i * 100 / DIVISIONS) + '% - 1px)',
          width: 2,
          background: '#ffffff',
          opacity: 0.55,
          pointerEvents: 'none',
        }}
      />
    )
  }

  return (
    <div
      className='lbb-root'
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 420,
        margin: '0 auto',
        paddingTop: 12,
      }}
      role='img'
      aria-label={liveAngle != null ? `Angle ${Math.round(liveAngle)} degrees` : 'Range bar'}
    >
      {showTick && tickPct != null && (
        // Tick mirrors RomBandDial: same red (#e53935 — admin lacks the
        // cssVar module so the hex is inlined), same ~12 × 10 footprint.
        // Points DOWN at the bar.
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 'calc(' + tickPct + '% - 6px)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '10px solid #e53935',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      )}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: height,
          background: 'rgba(255,255,255,0.10)',
          border: '2px solid #ffffff',
          borderRadius: height / 2,
          overflow: 'hidden',
        }}
      >
        {segments}
        {dividers}
      </div>
    </div>
  )
}
